import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { Object3D } from 'three';
import { useReducedMotion } from './hooks/useReducedMotion';
import { useWebGLSupport } from './hooks/useWebGLSupport';

// Global 3D capability + a registry of named mount points. This lives OUTSIDE
// the <Canvas> (plain DOM context) so non-3D UI can ask "is 3D active?" and so
// future interactive avatars can attach to a named anchor without prop-drilling
// through the scene graph. Today the registry is unused at runtime — it is the
// seam that lets avatars drop in later without rearchitecting.

export interface ThreeContextValue {
  /** WebGL is available AND the user has not requested reduced motion. */
  canRender3D: boolean;
  webglSupported: boolean;
  reducedMotion: boolean;
  registerMountPoint: (id: string, object: Object3D | null) => void;
  getMountPoint: (id: string) => Object3D | null;
}

const ThreeContext = createContext<ThreeContextValue | null>(null);

export function ThreeProvider({ children }: { children: ReactNode }) {
  const webglSupported = useWebGLSupport();
  const reducedMotion = useReducedMotion();
  const mountPoints = useRef(new Map<string, Object3D>());

  const registerMountPoint = useCallback((id: string, object: Object3D | null) => {
    if (object) mountPoints.current.set(id, object);
    else mountPoints.current.delete(id);
  }, []);

  const getMountPoint = useCallback((id: string) => mountPoints.current.get(id) ?? null, []);

  const value = useMemo<ThreeContextValue>(
    () => ({
      canRender3D: webglSupported && !reducedMotion,
      webglSupported,
      reducedMotion,
      registerMountPoint,
      getMountPoint,
    }),
    [webglSupported, reducedMotion, registerMountPoint, getMountPoint],
  );

  return <ThreeContext.Provider value={value}>{children}</ThreeContext.Provider>;
}

export function useThree3D(): ThreeContextValue {
  const ctx = useContext(ThreeContext);
  if (!ctx) throw new Error('useThree3D must be used within <ThreeProvider>');
  return ctx;
}

/** Non-throwing accessor — safe to call from inside the R3F canvas tree. */
export function useThree3DOptional(): ThreeContextValue | null {
  return useContext(ThreeContext);
}
